import React, { useState } from 'react'
import { Button, Image, Modal } from 'semantic-ui-react'
import archive_icon from '../../assets/svg/archive_button.svg'
import trash_icon from '../../assets/svg/trash.svg'
import update_icon from '../../assets/svg/update_confirmation.svg'
import { useStateFromProp } from '../../hooks/useStateFromProps'

interface Props {
  open: boolean
  closeModal?: boolean
  confirmUpdateModal?: boolean
  confirmUpdateCustomMsg?: string
  confirmUpdateCustomTitle?: string
  confirmUpdateContent?: JSX.Element
  disabled?: boolean
  archived?: boolean
  userInput: (action: 'cancel' | 'delete') => void
  customDeleteMsg?: string
  hideConfirmButton?: boolean
}

const ConfirmDeleteModal = (props: Props): JSX.Element => {
  const {
    open,
    closeModal,
    confirmUpdateModal,
    confirmUpdateContent,
    disabled,
    archived,
    userInput,
    customDeleteMsg,
    confirmUpdateCustomMsg,
    confirmUpdateCustomTitle,
    hideConfirmButton
  } = props

  const [_open, _setOpen] = useStateFromProp(open)
  const [_checked, _setChecked] = useState(false)

  const handleCancel = () => {
    _setOpen(false)
    _setChecked(false)
    userInput('cancel')
  }

  const handleDelete = () => {
    _setOpen(false)
    _setChecked(false)
    userInput('delete')
  }

  const handleCheck = () => {
    if (_checked) {
      _setChecked(false)
    } else {
      _setChecked(true)
    }
  }

  const renederCheckbox = (custom_text: string) => {
    return (
      <div style={{ display: 'flex', margin: '.5rem auto' }}>
        {_checked ?
          <Button
            style={{ color: 'white', height: '1.25rem', width: '1.25rem', borderRadius: '0.3rem', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#FA5953' }}
            icon='check'
            onClick={handleCheck}
          /> :
        <div
          style={{ cursor: 'pointer', marginRight: '0.25rem', border: '1px solid #E5EBF0', height: '1.55rem', width: '1.55rem', borderRadius: '0.3rem' }}
          onClick={handleCheck}
        />}
        <p style={{ marginLeft: '.5rem' }}>{custom_text}</p>
      </div>
    )
  }

  const renderArchived = () => {
    return {
      title: <p>Archive Confirmation</p>,
      question: <p style={{ margin: 0 }}>Are you sure you want to close these records?</p>,
      content: <p>Archived projects can be reopened at any point.</p>,
      checkbox: renederCheckbox('I confirm the archiving.'),
      button: 'Archive'
    }
  }

  const renderDisabled = () => {
    return {
      title: <p>Delete Confirmation</p>,
      question: null,
      content: <p>Only tasks with no existing time tracking entries can be deleted. To delete this task, please delete or change any time tracking entries related to it first.</p>,
      checkbox: null,
      button: 'Delete'
    }
  }

  const renderConfirmUpdate = () => {
    return {
      title: (confirmUpdateCustomTitle ?? <p>Confirm Update</p>),
      question: (confirmUpdateCustomMsg ?? <p style={{ margin: 0 }}>Are you sure you want to update these records?</p>),
      content: confirmUpdateContent,
      checkbox: null,
      button: 'Confirm'
    }
  }

  const renderClose = () => {
    return {
      title: <p>Close Confirmation</p>,
      question: <p style={{ margin: 0 }}>Are you sure you want to close these records?</p>,
      content: null,
      checkbox: null,
      button: 'Close'
    }
  }

  const renderDeleteMsg = () => {
    if (customDeleteMsg) {
      return (<p style={{ margin: 0 }}>{customDeleteMsg}</p>)
    }
    return (<p style={{ margin: 0 }}>Are you sure you want to delete these records?</p>)
  }

  const renderDelete = () => {
    return {
      title: <p>Delete Confirmation</p>,
      question: renderDeleteMsg() ,
      content: customDeleteMsg ? null : <p>This process cannot be undone.</p>,
      checkbox: renederCheckbox('I confirm the deletion.'),
      button: 'Delete'
    }
  }

  const renderModalHeader = () => {
    if (confirmUpdateModal) {
      return renderConfirmUpdate().title
    }
    if (closeModal) {
      return renderClose().title
    }
    if (archived) {
      return renderArchived().title
    }
    if (disabled) {
      return renderDisabled().title
    }
    return renderDelete().title
  }


  const renderModalQuestion = () => {
    if (confirmUpdateModal) {
      return renderConfirmUpdate().question
    }
    if (closeModal) {
      return renderClose().question
    }
    if (archived) {
      return renderArchived().question
    }
    if (disabled) {
      return renderDisabled().question
    }
    return renderDelete().question
  }

  const renderModalDescription = () => {
    if (confirmUpdateModal) {
      return renderConfirmUpdate().content
    }
    if (closeModal) {
      return renderClose().content
    }
    if (archived) {
      return renderArchived().content
    }
    if (disabled) {
      return renderDisabled().content
    }
    return renderDelete().content
  }

  const renderModalCheckbox = () => {
    if (confirmUpdateModal) {
      return renderConfirmUpdate().checkbox
    }
    if (closeModal) {
      return renderClose().checkbox
    }
    if (archived) {
      return renderArchived().checkbox
    }
    if (disabled) {
      return renderDisabled().checkbox
    }
    return renderDelete().checkbox
  }

  const renderModalButton = () => {
    if (confirmUpdateModal) {
      return renderConfirmUpdate().button
    }
    if (closeModal) {
      return renderClose().button
    }
    if (archived) {
      return renderArchived().button
    }
    if (disabled) {
      return renderDisabled().button
    }
    return renderDelete().button
  }

  return (
    <Modal
      size='tiny'
      open={_open}
      closeOnDimmerClick={false}
      onClose={() => _setOpen(false)}
      onOpen={() => _setOpen(true)}
    >
      <Modal.Header style={{ fontSize: '1.8rem', display: 'flex', justifyContent: 'center', flexDirection: 'column', fontWeight: 500, textAlign: 'center' }}>
        <Image src={confirmUpdateModal ? update_icon : archived ? archive_icon : trash_icon} style={{ height: '3.5rem', marginBottom: '1rem' }} />
        {renderModalHeader()}
      </Modal.Header>
      <Modal.Content style={{ fontSize: '1.1rem', padding: '0rem 3rem 0 3rem', textAlign: 'center' }}>
        <Modal.Description style={{ display: 'flex', flexDirection: 'column' }}>
          {renderModalQuestion()}
          {renderModalDescription()}
          {renderModalCheckbox()}
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions style={{ display: 'flex', justifyContent: 'center' }}>
        <Button style={{ borderRadius: '5px' }} basic={true} onClick={() => handleCancel()}>Cancel</Button>
        {!hideConfirmButton && <Button disabled={!_checked && !closeModal && !confirmUpdateModal} style={{ borderRadius: '5px' }} color={!confirmUpdateModal ? 'red' : undefined} primary={confirmUpdateModal} onClick={() => handleDelete()}><p>{renderModalButton()}</p></Button>}
      </Modal.Actions>
    </Modal>
  )
}

export default ConfirmDeleteModal
